import { createApp } from 'vue'
import App from './App.vue'
import { router } from './helpers'
import { store } from './store'
import VueApexCharts from 'vue3-apexcharts'
import * as BootstrapVueNext from 'bootstrap-vue-next'
import mitt from 'mitt'

// Import Bootstrap and BootstrapVueNext CSS
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import '@/assets/colors.css'
import '@/assets/css/custom.scss'
import '@/assets/css/extras.scss'

import '@/modules/workspace/styles.css'

// FontAwesome
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faSync, faFill, faTint, faAdjust, faSun, faExpand, faChevronLeft, faChevronRight, faHome,
  faSortAmountDown, faSortNumericDown, faSortNumericUp, faSortAmountUp, faFilter, faLungs,
  faSignOutAlt, faSearch, faCog, faBell, faExclamationTriangle, faHeartbeat, faExpandArrowsAlt,
  faCheckCircle, faTimesCircle, faPlus, faPoll, faTrash, faPen, faUpload, faUndo, faRedo,
  faCheck, faTimes, faDna, faEye, faEyeSlash, faUserLock, faCheckSquare, faSquare, faCommentDots,
  faInfo, faCircle, faPhotoVideo, faClone, faMicrochip, faCrop, faCogs, faHourglassStart,
  faChartArea, faClipboardList, faUsersCog, faSitemap, faCheckDouble, faChevronDown, faChevronUp,
  faFileCsv, faFileCode, faFileAlt, faCamera, faArrowCircleRight, faIdBadge, faDotCircle,
  faDownload, faChartLine, faPeopleArrows, faCommentMedical, faEdit, faInfoCircle, faPencilAlt, faArrowsAlt,
  faMinus, faClock, faChartBar, faArrowUp, faArrowDown, faHeart, faImage, faImages, faTools, faSave, faRobot, faTags, faPencil, faStar,
  faFolderPlus, faKey, faLifeRing, faBook, faFolder, faLock, faRightFromBracket, faTh, faList, faPlusCircle,
  faLaptopCode, faBrain, faMobileAlt, faDatabase, faClockRotateLeft, faArrowRight, faXRay, faRadiation, faWaveSquare, faCalendarDay,
  faFileImport, faFileMedical, faSliders, faUsers, faVirus, faStethoscope, faLayerGroup, faCodeCompare, faUserDoctor,
  faGaugeHigh, faGaugeSimple, faArrowTrendUp, faDisease, faShield, faBolt, faServer, faMagnet, faUserCheck, faChartColumn, faFileLines, faHeartPulse,
  faAngleDoubleLeft, faAngleDoubleRight, faWindowRestore, faSquarePlus, faListCheck, faChartSimple, faTable, faSpinner,
  faCircleLeft, faCircleRight, faBars, faXmark, faBan, faPaperPlane, faTag
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import socketio from './helpers/socket-client'
import FileUpload from './components/FileUpload.vue'

// Add icons to library
library.add(faInfoCircle, faPencilAlt, faArrowsAlt, faCommentMedical, faEdit, faSync, faFill, faTint, faAdjust, faSun, faEye, faEyeSlash, faExpand, faChevronLeft, faChevronRight, faHome, faLungs, faSignOutAlt, faSearch, faCog, faBell, faExclamationTriangle, faHeartbeat, faExpandArrowsAlt, faCheckCircle, faTimesCircle, faPlus, faPoll, faTrash, faPen, faUpload, faUndo, faRedo, faCheck, faTimes, faDna, faEye, faEyeSlash, faUserLock, faCheckSquare, faSquare, faCommentDots, faInfo, faCircle)
library.add(faCamera, faFilter, faSortAmountDown, faSortNumericDown, faSortNumericUp, faSortAmountUp, faPhotoVideo, faClone, faMicrochip, faCrop, faCheckDouble, faCogs, faHourglassStart, faChartArea, faClipboardList, faUsersCog, faSitemap, faChevronDown, faChevronUp, faFileCsv, faFileCode, faFileAlt, faArrowCircleRight, faIdBadge, faDotCircle, faDownload)
library.add(faChartLine, faPeopleArrows, faMinus, faClock, faChartBar, faArrowUp, faArrowDown, faHeart, faImage, faImages, faTools, faSave, faRobot, faTags, faPencil, faStar)
library.add(faFolderPlus, faKey, faLifeRing, faBook, faFolder, faLock, faRightFromBracket, faTh, faList, faPlusCircle, faLaptopCode, faBrain, faMobileAlt, faDatabase, faPaperPlane, faTag)
library.add(faClockRotateLeft, faArrowRight, faXRay, faMagnet, faRadiation, faWaveSquare, faCalendarDay, faAngleDoubleLeft, faAngleDoubleRight, faWindowRestore, faSquarePlus, faCircleLeft, faCircleRight, faBars, faXmark, faBan)
library.add(faFileImport, faFileMedical, faSliders, faUsers, faVirus, faStethoscope, faLayerGroup, faCodeCompare, faUserDoctor, faGaugeHigh, faGaugeSimple, faArrowTrendUp, faDisease, faShield, faBolt, faServer, faUserCheck, faChartColumn, faFileLines, faHeartPulse, faListCheck, faChartSimple, faTable, faSpinner)

// Create Vue app
const app = createApp(App)

// Create event emitter
const emitter = mitt()

// Use plugins
app.use(router)
app.use(store)
app.use(BootstrapVueNext.createBootstrap())

// Register all Bootstrap Vue Next components
Object.keys(BootstrapVueNext).forEach(key => {
  if (key.startsWith('B') && key !== 'BootstrapVueNextResolver' && key.charAt(1) === key.charAt(1).toUpperCase()) {
    app.component(key, BootstrapVueNext[key])
  }
})

app.component('apexchart', VueApexCharts)
app.component('font-awesome-icon', FontAwesomeIcon)
app.component('file-upload', FileUpload)

// Global properties
app.config.globalProperties.$emitter = emitter
app.config.globalProperties.$socketio = socketio

// Custom draggable directive
app.directive('draggable', {
  mounted (el) {
    let header = el.querySelector('.slidein-header')
    if (!header) {
      header = document.createElement('div')
      header.className = 'slidein-header'

      const closeButton = el.querySelector('.float-right')
      if (closeButton) {
        const titleSpan = document.createElement('span')
        header.appendChild(titleSpan)
        closeButton.parentNode.removeChild(closeButton)
        header.appendChild(closeButton)
      }

      el.insertBefore(header, el.firstChild)
    }

    let initialX, initialY, initialTop, initialLeft, initialRight

    const clearStyles = () => {
      if (!el.classList.contains('open')) {
        el.style.left = ''
        el.style.right = ''
        el.style.top = ''
      }
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          clearStyles()
        }
      })
    })

    observer.observe(el, { attributes: true })

    header.onmousedown = (e) => {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'I') return

      e.preventDefault()

      initialX = e.clientX
      initialY = e.clientY

      const style = window.getComputedStyle(el)
      initialTop = parseInt(style.top)
      initialLeft = parseInt(style.left)
      initialRight = parseInt(style.right)

      const viewportWidth = window.innerWidth
      const elementRect = el.getBoundingClientRect()

      // Calculate position based on current positioning
      if (!isNaN(initialRight) && initialRight !== 0) {
        // Element is positioned with 'right' property
        initialRight = viewportWidth - (elementRect.left + elementRect.width)
      } else {
        // Element is positioned with 'left' property or default
        initialLeft = elementRect.left
        initialRight = null
      }

      el.style.transition = 'none'

      document.onmousemove = (e) => {
        const deltaX = e.clientX - initialX
        const deltaY = e.clientY - initialY
        const newTop = initialTop + deltaY

        if (initialRight !== null) {
          // Using right positioning
          const newRight = initialRight - deltaX
          el.style.top = `${newTop}px`
          el.style.right = `${newRight}px`
          el.style.left = 'auto'
        } else {
          // Using left positioning
          const newLeft = initialLeft + deltaX
          el.style.top = `${newTop}px`
          el.style.left = `${newLeft}px`
          el.style.right = 'auto'
        }
        el.style.position = 'fixed'
      }

      document.onmouseup = () => {
        el.style.transition = ''
        document.onmousemove = null
        document.onmouseup = null
      }
    }

    const closeButton = el.querySelector('.float-right')
    if (closeButton) {
      const originalClick = closeButton.onclick
      closeButton.onclick = function (e) {
        if (originalClick) originalClick.call(this, e)
        setTimeout(clearStyles, 0)
      }
    }

    el._observer = observer
  },
  unmounted (el) {
    const observer = el._observer
    if (observer) {
      observer.disconnect()
    }
  }
})

// Mount app
app.mount('#app')
